import React, { useCallback, useState } from 'react';
import { GoogleMap, useLoadScript, StandaloneSearchBox } from '@react-google-maps/api';
import { CollectionPointsMarkers } from './map-markers';

const GREECE_BOUNDS = {
  north: 44.108926,
  south: 33.957559,
  west: 18.17496,
  east: 32.061679,
};

const STARTING_ZOOM = 17;

/* ---------------- Map ------------------ */
const containerStyle = {
  width: '100%',
  height: '100vh',
};
const libraries = ['places'];

/* Map Component */
export const Map = ({ center: initCenter, initZoom = STARTING_ZOOM, isRechargeGreece }) => {
  const { isLoaded, loadError } = useLoadScript({
    version: 'beta',
    //language: 'greek',
    // googleMapsApiKey: 'AIzaSyDiWddhesll06S20JkczyBJWrGkkWzqHzo', //p.tarrazi@motion.gr 2025
    googleMapsApiKey: 'AIzaSyBvkQGyg9dc87QFK8n3y20lbD2MVcb79jg', // afis 2025-10-23
    // ...otherOptions
    libraries: libraries as any,
  });
  const [map, setMap] = React.useState(null);

  const [center, setCenter] = useState(initCenter);
  const onCenterChanged = useCallback(() => {
    if (map) {
      const c = map.getCenter().toJSON();
      setCenter(c);
    }
  }, [map]);

  const [zoom, setZoom] = useState(initZoom);
  const onZoomChanged = useCallback(() => {
    if (map) setZoom(map.getZoom());
  }, [map]);

  const [bounds, setBounds] = useState<any>();
  let boundsDelayChange;
  const onBoundsChanged = useCallback(() => {
    if (map) {
      const b = map.getBounds();
      const ne = b.getNorthEast();
      const sw = b.getSouthWest();
      const bounds = {
        fromLat: sw.lat(),
        tillLat: ne.lat(),
        fromLng: sw.lng(),
        tillLng: ne.lng(),
      };
      console.log('bounds changed = ', bounds, b);
      clearTimeout(boundsDelayChange);
      boundsDelayChange = setTimeout(() => setBounds(bounds), 500);
    }
  }, [map]);

  /* ---------- Place Search ----------------- */
  const [placeMarker, setPlaceMarker] = useState(null);
  const clearPreviousMarker = useCallback(() => {
    if (placeMarker) {
      placeMarker.setMap(null);
      setPlaceMarker(null);
    }
  }, [placeMarker]);
  const [searchBox, setSearchBox] = useState(null);
  const onLoad = (ref) => setSearchBox(ref);
  const onPlacesChanged = () => {
    // Clear Prv Marker
    clearPreviousMarker();

    const places = searchBox.getPlaces();

    if (!places || places.length === 0) return;
    const name = places[0].name;
    const loc = places[0].geometry.location;
    map.setCenter(loc);
    map.setZoom(19);

    // Add Marker
    const mrk = new google.maps.Marker({
      map: map,
      label: name,
      title: name,
      position: loc,
    });
    setPlaceMarker(mrk);
  };

  const onMarkerClick = (latLng) => {
    if (zoom > 17) return;
    map.setCenter(latLng);
    if (zoom > 15) map.setZoom(zoom + 1);
    else if (zoom < 21) map.setZoom(zoom + 2);
  };

  // Recharge Greece
  const [markerFilter, setMarkerFilter] = useState<'all' | 'recharge' | 'nonrecharge'>('all');

  // Render --------------------------------------
  if (loadError) {
    return <div>Εμφανίστηκε πρόβλημα.</div>;
  } else if (!isLoaded) return <div>Loading...</div>;
  else
    return (
      <GoogleMap
        onLoad={(map) => {
          console.log('map loaded', map);
          setMap(map);
        }}
        mapContainerStyle={containerStyle}
        center={initCenter}
        onCenterChanged={onCenterChanged}
        zoom={initZoom}
        onZoomChanged={onZoomChanged}
        onBoundsChanged={onBoundsChanged}
        options={{ restriction: { latLngBounds: GREECE_BOUNDS, strictBounds: false }, minZoom: 6 }}
      >
        {/* Filter buttons */}
        <div
          style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            zIndex: 1000,
            display: 'flex',
            gap: 8,
          }}
        >
          <button
            onClick={() => setMarkerFilter('recharge')}
            style={{
              padding: '6px 10px',
              borderRadius: 4,
              border: '1px solid rgba(0,0,0,0.2)',
              background: markerFilter === 'recharge' ? '#0d6efd' : 'white',
              color: markerFilter === 'recharge' ? 'white' : 'black',
            }}
            title="Show only recharge points"
          >
            Recharge Greece
          </button>
          <button
            onClick={() => setMarkerFilter('nonrecharge')}
            style={{
              padding: '6px 10px',
              borderRadius: 4,
              border: '1px solid rgba(0,0,0,0.2)',
              background: markerFilter === 'nonrecharge' ? '#0d6efd' : 'white',
              color: markerFilter === 'nonrecharge' ? 'white' : 'black',
            }}
            title="Show only non-recharge points"
          >
            Κάδοι Αφής
          </button>
          <button
            onClick={() => setMarkerFilter('all')}
            style={{
              padding: '6px 10px',
              borderRadius: 4,
              border: '1px solid rgba(0,0,0,0.2)',
              background: markerFilter === 'all' ? '#0d6efd' : 'white',
              color: markerFilter === 'all' ? 'white' : 'black',
            }}
            title="Show all points"
          >
            Όλα
          </button>
        </div>
        <StandaloneSearchBox onLoad={onLoad} onPlacesChanged={onPlacesChanged} bounds={GREECE_BOUNDS}>
          <input
            type="search"
            placeholder="Εύρεση διεύθυνσης"
            style={{
              boxSizing: `border-box`,
              border: `1px solid transparent`,
              height: `32px`,
              padding: `0 12px`,
              borderRadius: `3px`,
              boxShadow: `0 2px 6px rgba(0, 0, 0, 0.3)`,
              fontSize: `14px`,
              outline: `none`,
              textOverflow: `ellipses`,
              position: 'absolute',
              top: '12px',
              left: '42%',
              width: `35%`,
              marginLeft: '-120px',
            }}
            onChange={(el) => {
              if (!el.currentTarget.value) clearPreviousMarker();
            }}
          />
        </StandaloneSearchBox>

        <CollectionPointsMarkers
          isRechargeGreece={isRechargeGreece}
          zoom={zoom}
          bounds={bounds}
          onMarkerClick={onMarkerClick}
          filter={markerFilter}
        />
      </GoogleMap>
    );
};
